import {NS} from '../namespaces.js'

const resources = {
  en: {
    [NS.FORECAST_ROLLOUT]: {
      title: 'Forecast rollout control',
      description: 'Monitor bounded rollout eligibility and run operator checks without leaving the admin shell.',
      actions: {
        refresh: 'Refresh',
        runHealthCheck: 'Run health check',
      },
      policy: {
        title: 'Slice policy',
        description: 'This forecast rollout slice is closed as bounded by design. This page is for observing and maintaining that boundary, not widening it implicitly.',
        alert: 'Broad mixed-currency rollout is out of scope for this slice. Limited rollout only stays active for the explicitly persisted cohort.',
        labels: {
          sliceStatus: 'Slice status',
          runtimeBoundary: 'Runtime boundary',
          latestBoundaryMode: 'Latest boundary mode',
          cohortSize: 'Persisted cohort size',
        },
        values: {
          boundedByDesign: 'bounded_by_design',
          explicitCohortOnly: 'explicit_user_ids_only',
        },
      },
      health: {
        title: 'Health check',
        description: 'Run one bounded rollout check that refreshes readiness, verifies one candidate, and returns a single verdict.',
        runError: 'Failed to run health check.',
        fields: {
          status: 'Health status',
          continueBoundedRollout: 'Can continue bounded rollout',
          fxSource: 'Effective FX source',
          targetUser: 'Verification target',
          reason: 'Reason',
          warnings: 'Warnings',
          latestDecision: 'Latest decision',
          boundaryMode: 'Boundary mode',
          cohortSize: 'Cohort size',
          refreshDecision: 'Refresh decision',
          syntheticStatus: 'Synthetic status',
        },
        emptyWarnings: 'No health warnings.',
      },
      telemetry: {
        title: 'Telemetry',
        description: 'Latest rollout totals, readiness posture, and notable execution events.',
        labels: {
          latestDecision: 'Latest decision',
          totalRequests: 'Forecast requests',
          successCount: 'Successful requests',
          limitedRolloutHits: 'Limited-rollout hits',
          limitedRolloutFailures: 'Limited-rollout failures',
          blockedMixedCurrency: 'Mixed-currency guard blocks',
          syntheticEligible: 'Synthetic eligible',
        },
        alertsTitle: 'Alerts',
        emptyEvents: 'No notable rollout events yet.',
        emptyAlerts: 'No rollout alerts.',
        fields: {
          user: 'User',
          blockedCode: 'Blocked code',
        },
      },
      decisions: {
        title: 'Recent decisions',
        description: 'Operator decisions recorded with the evidence snapshot used at the time.',
        empty: 'No rollout decisions recorded yet.',
        fields: {
          cohortSizeTag: 'cohort {{count}}',
        },
        record: {
          title: 'Record decision',
          description: 'Persist a guarded rollout decision with the current evidence snapshot. Candidate limited rollout remains locked to the explicit proven cohort only.',
          decisionLabel: 'Decision',
          fxSourceLabel: 'FX source override',
          noteLabel: 'Operator note',
          notePlaceholder: 'Why is this safe right now?',
          proveCompositeLabel: 'Re-run composite proof before saving',
          submit: 'Record decision',
          success: 'Decision recorded.',
          error: 'Failed to record rollout decision.',
          options: {
            hold: 'Hold',
            continueShadowValidation: 'Continue shadow validation',
            candidateLimitedRollout: 'Candidate limited rollout',
          },
        },
      },
      synthetic: {
        title: 'Synthetic check',
        description: 'Verify one candidate user against the limited-rollout gate without depending on organic traffic.',
        userIdLabel: 'User ID',
        userIdPlaceholder: 'Enter a user id',
        submit: 'Run synthetic check',
        fields: {
          status: 'Verification status',
          eligible: 'Eligible for limited rollout',
          executionMode: 'Execution mode',
          fxSource: 'FX source',
          classification: 'Classification',
          quoteCounts: 'Quote counts',
          startingBalance: 'Starting balance',
          outcome: 'Outcome',
        },
        recentTitle: 'Recent synthetic checks',
        emptyRecent: 'No synthetic checks recorded yet.',
        eligible: {
          yes: 'Yes',
          no: 'No',
        },
        quoteCounts: 'fresh {{fresh}}, stale {{stale}}, missing {{missing}}, failed {{failed}}',
        runError: 'Failed to run synthetic check.',
      },
      status: {
        loading: 'Loading rollout telemetry…',
        loadError: 'Failed to load forecast rollout telemetry.',
      },
    },
  },
  uk: {
    [NS.FORECAST_ROLLOUT]: {
      title: 'Керування роллаутом прогнозу',
      description: 'Відстежуйте bounded rollout і запускайте operator checks прямо з адмінського інтерфейсу.',
      actions: {
        refresh: 'Оновити',
        runHealthCheck: 'Запустити health check',
      },
      policy: {
        title: 'Політика slice',
        description: 'Цей forecast rollout slice закрито як bounded by design. Сторінка потрібна для спостереження і підтримки цієї межі, а не для неявного widening.',
        alert: 'Broad mixed-currency rollout не входить у цей slice. Limited rollout лишається активним тільки для явно збереженого cohort.',
        labels: {
          sliceStatus: 'Статус slice',
          runtimeBoundary: 'Runtime boundary',
          latestBoundaryMode: 'Останній boundary mode',
          cohortSize: 'Розмір збереженого cohort',
        },
        values: {
          boundedByDesign: 'bounded_by_design',
          explicitCohortOnly: 'explicit_user_ids_only',
        },
      },
      health: {
        title: 'Health check',
        description: 'Запустіть одну bounded rollout перевірку, яка оновлює readiness, верифікує одного кандидата й повертає єдиний verdict.',
        runError: 'Не вдалося виконати health check.',
        fields: {
          status: 'Статус health check',
          continueBoundedRollout: 'Можна продовжувати bounded rollout',
          fxSource: 'Effective FX source',
          targetUser: 'Цільовий користувач перевірки',
          reason: 'Причина',
          warnings: 'Попередження',
          latestDecision: 'Останнє рішення',
          boundaryMode: 'Boundary mode',
          cohortSize: 'Розмір cohort',
          refreshDecision: 'Рішення після refresh',
          syntheticStatus: 'Synthetic status',
        },
        emptyWarnings: 'Попереджень по health check немає.',
      },
      telemetry: {
        title: 'Телеметрія',
        description: 'Актуальні підсумки роллауту, readiness posture та помітні execution events.',
        labels: {
          latestDecision: 'Останнє рішення',
          totalRequests: 'Forecast requests',
          successCount: 'Успішні запити',
          limitedRolloutHits: 'Limited-rollout хіти',
          limitedRolloutFailures: 'Limited-rollout помилки',
          blockedMixedCurrency: 'Блокування mixed-currency guard',
          syntheticEligible: 'Synthetic eligible',
        },
        alertsTitle: 'Алерти',
        emptyEvents: 'Поки немає помітних подій роллауту.',
        emptyAlerts: 'Алертів по роллауту немає.',
        fields: {
          user: 'Користувач',
          blockedCode: 'Код блокування',
        },
      },
      decisions: {
        title: 'Останні рішення',
        description: 'Операторські рішення, збережені разом зі snapshot доказів.',
        empty: 'Рішень по роллауту ще немає.',
        fields: {
          cohortSizeTag: 'cohort {{count}}',
        },
        record: {
          title: 'Зафіксувати рішення',
          description: 'Збережіть guarded rollout decision разом із поточним snapshot доказів. Candidate limited rollout лишається прив’язаним тільки до явно доведеного cohort.',
          decisionLabel: 'Рішення',
          fxSourceLabel: 'Перевизначення FX source',
          noteLabel: 'Нотатка оператора',
          notePlaceholder: 'Чому це безпечно прямо зараз?',
          proveCompositeLabel: 'Повторно виконати composite proof перед збереженням',
          submit: 'Зберегти рішення',
          success: 'Рішення збережено.',
          error: 'Не вдалося зберегти rollout decision.',
          options: {
            hold: 'Hold',
            continueShadowValidation: 'Продовжити shadow validation',
            candidateLimitedRollout: 'Кандидат на limited rollout',
          },
        },
      },
      synthetic: {
        title: 'Synthetic check',
        description: 'Перевірте одного candidate user на limited-rollout gate без залежності від органічного трафіку.',
        userIdLabel: 'User ID',
        userIdPlaceholder: 'Введіть user id',
        submit: 'Запустити synthetic check',
        fields: {
          status: 'Статус перевірки',
          eligible: 'Підходить для limited rollout',
          executionMode: 'Execution mode',
          fxSource: 'FX source',
          classification: 'Класифікація',
          quoteCounts: 'Стан котирувань',
          startingBalance: 'Стартовий баланс',
          outcome: 'Результат',
        },
        recentTitle: 'Останні synthetic checks',
        emptyRecent: 'Synthetic checks ще не виконувалися.',
        eligible: {
          yes: 'Так',
          no: 'Ні',
        },
        quoteCounts: 'fresh {{fresh}}, stale {{stale}}, missing {{missing}}, failed {{failed}}',
        runError: 'Не вдалося виконати synthetic check.',
      },
      status: {
        loading: 'Завантаження телеметрії роллауту…',
        loadError: 'Не вдалося завантажити телеметрію роллауту прогнозу.',
      },
    },
  },
}

export default resources
